// EmbedPortalContext — exposes the embed's DOM root to descendants.
//
// Why this exists: every dialog / popover / lightbox / toast in the
// chat surface used to portal to `document.body`, which broke the
// embed's style-isolation contract — overlays escaped the embed's
// bounding box and leaked into the host page. This context lets a
// chat surface publish its root DOM node so descendants can portal
// into it instead.
//
// Two registered surfaces use the same shape:
//
//   - The embed (`EmbeddableChat` inside `EmbeddableChatProvider`)
//     registers its outer `<div>` so overlays render inside the
//     embed's bounding box.
//   - The standalone Tauri/web shell (`App.tsx`) registers its
//     outer `<div>` for the same reason — without this, body-rooted
//     portals would render outside the `[data-chat-root]` font
//     boundary and lose the chat font stack.
//
// Anatomy:
//
//   - `<EmbedPortalProvider>` holds the root in state. Sits at the
//     top of both subtrees (embed provider + standalone App) so every
//     descendant (MatrixProvider, ToastProvider, dialogs) reads from
//     the same value.
//   - `useRegisterEmbedRoot()` returns the registration fn; the chat
//     surface calls it on mount and passes `null` on unmount. The
//     register fn is a no-op outside the provider so an isolated
//     render (Storybook fragments, tests) doesn't throw.
//   - `usePortalRoot()` is the consumer hook for portal targets. It
//     returns the registered root when present, `document.body` as a
//     fallback for isolated renders, and `null` on the server.
//
// Overlay containment mechanism: the chat surface's outer `<div>`
// carries `position: relative` (the embed additionally sets
// `overflow: hidden` to clip overflow into the host; the standalone
// shell fills the viewport so it doesn't need to). Overlays portalled
// into the surface switch their scrim from `position: fixed` to
// `position: absolute` only when the portal target is the actual
// embed (see `isEmbeddedPortal` in `lib/portal-positioning.ts`),
// which makes the scrim's `inset: 0` resolve against the embed's box
// rather than the viewport. The standalone surface keeps the viewport
// scrim — both modes are equivalent there because the standalone root
// fills the screen — but `ImageLightbox`'s `document.body` scroll lock
// then runs unconditionally in standalone and is suppressed inside an
// embed (mutating `document.body.style` is a style-isolation
// violation).
//
// We deliberately do NOT use `transform: translate(0)` to create a
// containing block — that would also create a new stacking context
// and is unnecessary given the per-overlay flip.

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

interface ContextValue {
  /** The registered chat-surface root — the embed's wrapper inside
   *  `<EmbeddableChat>`, or the standalone shell's `<div>` in
   *  `App.tsx`. Null when no surface has registered yet (initial
   *  render, isolated component renders without the provider). */
  root: HTMLElement | null;
  /** Called by the chat surface on mount to publish its root, and on
   *  unmount with `null` to clear it. */
  setRoot: (el: HTMLElement | null) => void;
}

const EmbedPortalContext = createContext<ContextValue | null>(null);

/**
 * Provider for the chat surface's portal root. Both the embeddable
 * provider (`EmbeddableChatProvider`) and the standalone shell
 * (`App.tsx`) wrap their subtree with this provider so dialogs,
 * popovers, the lightbox, and the toast viewport portal inside the
 * chat surface's `[data-chat-root]` / `[data-nurion-chat-embed]`
 * font boundary. Isolated callers (Storybook fragments, vitest) can
 * skip the provider; `usePortalRoot()` then falls back to
 * `document.body`.
 *
 * The root is held in state (not a ref) so consumers re-render when
 * the descendant chat surface mounts and registers its element.
 * Without this, `<ToastProvider>` (which renders before the surface
 * mounts) would observe a stale `null` and never portal into the
 * surface.
 */
export function EmbedPortalProvider({ children }: { children: ReactNode }) {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  // setRoot is referentially stable from useState; memoising here keeps
  // descendants that depend on the value object from re-running effects
  // when only `root` flips.
  const value = useMemo<ContextValue>(() => ({ root, setRoot }), [root]);
  return <EmbedPortalContext.Provider value={value}>{children}</EmbedPortalContext.Provider>;
}

/**
 * The chat surface's registered root DOM node — the embed wrapper or
 * the standalone shell's `<div>`. Returns null when no surface has
 * registered (initial render, isolated render without the provider).
 * Callers that need a portal target should prefer `usePortalRoot()`,
 * which collapses the null case to `document.body`.
 */
export function useEmbedRoot(): HTMLElement | null {
  return useContext(EmbedPortalContext)?.root ?? null;
}

const noop = (_el: HTMLElement | null) => {};

/**
 * Returns the registration function the chat surface calls on mount
 * (with its root element) and on unmount (with `null`). Both
 * `<EmbeddableChat>` and `<StandaloneChatRoot>` (in `App.tsx`) wire
 * through this hook. Outside the provider this resolves to a no-op
 * so isolated component renders (Storybook fragments, vitest) work
 * without wiring the provider.
 */
export function useRegisterEmbedRoot(): (el: HTMLElement | null) => void {
  return useContext(EmbedPortalContext)?.setRoot ?? noop;
}

/**
 * Resolves the portal target for dialogs / popovers / lightbox /
 * toasts. Returns the registered chat surface (embed or standalone
 * shell) when the provider has one; falls back to `document.body`
 * for isolated callers that didn't mount the provider; null on the
 * server. Every call site already null-guards before invoking
 * `createPortal`.
 *
 * Note: this hook does not distinguish embed from standalone. Use
 * `isEmbeddedPortal` (in `lib/portal-positioning.ts`) when an
 * overlay needs to behave differently inside an actual embed (e.g.
 * `ImageLightbox`'s `document.body` scroll lock is suppressed inside
 * an embed because mutating host page styles is a style-isolation
 * violation, but runs in the standalone shell).
 */
export function usePortalRoot(): HTMLElement | null {
  const embedRoot = useEmbedRoot();
  if (embedRoot) return embedRoot;
  if (typeof document === "undefined") return null;
  return document.body;
}
